/**
 * aiProgressMessage — rotates a small set of progress messages
 * during a long-running AI call so the user has a sense the
 * request is making forward motion.
 *
 * Wave-4 audit fix (#10). The /rewrite and /polish endpoints
 * are non-streaming (single JSON response), so the user used
 * to see a static "Asking Claude…" with a pulsing icon for as
 * long as the round-trip took. On a 60s rewrite that became
 * indistinguishable from a hung request.
 *
 * This hook returns the current message + an elapsed-seconds
 * counter; callers render both. The messages escalate at fixed
 * milestones (ms-since-start) so the user gets reassurance at
 * 0s ("Reading your selection…"), 1s ("Asking Claude…"),
 * 4s ("Composing rewrite…") and 10s+ ("Still working — Claude
 * sometimes takes a moment for longer selections.").
 *
 * It's NOT real streaming — for that we'd need the backend to
 * emit SSE events. This is "perceived streaming" that gets us
 * 80% of the UX win for one frontend file.
 */
import { useEffect, useState } from 'react';

export interface ProgressFrame {
  /** The message to render. */
  message: string;
  /** Whole seconds elapsed since the call started — surface as
   *  "12s" next to the message at the longer milestones. */
  elapsedSeconds: number;
}

const STAGES: Array<{ atMs: number; message: string }> = [
  { atMs: 0, message: 'Reading your selection…' },
  { atMs: 1000, message: 'Asking Claude…' },
  { atMs: 4000, message: 'Composing rewrite…' },
  {
    atMs: 10_000,
    message:
      'Still working - Claude sometimes takes a moment for longer selections.',
  },
  {
    atMs: 25_000,
    message: 'Hang tight - almost there for big rewrites.',
  },
];

/**
 * @param active  Whether the call is in flight. The hook
 *                resets to stage 0 on the rising edge of
 *                `active` so back-to-back calls each restart
 *                their own progress timeline.
 */
export function useAiProgress(active: boolean): ProgressFrame {
  const [now, setNow] = useState<number>(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!active) {
      setStartedAt(null);
      setNow(0);
      return;
    }

    const start = Date.now();
    setStartedAt(start);
    setNow(0);

    const id = setInterval(() => {
      setNow(Date.now() - start);
    }, 250);

    return () => clearInterval(id);
  }, [active]);

  if (!active || startedAt == null) {
    return { message: STAGES[0].message, elapsedSeconds: 0 };
  }

  // Pick the latest stage whose atMs threshold is <= elapsed.
  let chosen = STAGES[0].message;
  for (const stage of STAGES) {
    if (now >= stage.atMs) chosen = stage.message;
  }
  return { message: chosen, elapsedSeconds: Math.floor(now / 1000) };
}
